// upcountry.js
// -----------------------------------------------------------------------------
// Up-country large-truck booking: the committed record of which provinces each
// trip group covers, and the pure rules over a pool of orders and the trips
// fixed from it. No Firestore, no Vue -- scripts/check-upcountry.js requires it.
// Change a group by editing GROUPS and re-running npm run test:upcountry.
// Design: docs/superpowers/specs/2026-10-02-upcountry-truck-booking-design.md
// -----------------------------------------------------------------------------
const moment = require('moment-timezone')
const R = require('./bookingRules')
const { ownedBy } = require('./people')
const I = require('./installTime')
const RJ = require('./returnJob')

// Three provinces sit where two routes meet and are in two groups on purpose:
// an order there counts toward both until a trip takes it.
const GROUPS = [
  { key: 'north-upper', label: 'เหนือบน', daysMin: 4, daysMax: 5,
    provinces: ['เชียงใหม่', 'เชียงราย', 'ลำปาง', 'ลำพูน', 'พะเยา', 'แพร่', 'น่าน', 'อุตรดิตถ์'] },
  { key: 'north-lower', label: 'เหนือล่าง', daysMin: 4, daysMax: 5,
    provinces: ['พิษณุโลก', 'สุโขทัย', 'ตาก', 'กำแพงเพชร', 'พิจิตร', 'เพชรบูรณ์', 'นครสวรรค์'] },
  { key: 'isan-north', label: 'อีสานเหนือ', daysMin: 5, daysMax: 7,
    provinces: ['ชัยภูมิ', 'อุดรธานี', 'หนองคาย', 'บึงกาฬ', 'หนองบัวลำภู', 'เลย', 'สกลนคร', 'นครพนม', 'ขอนแก่น', 'กาฬสินธุ์'] },
  { key: 'isan-south', label: 'อีสานใต้', daysMin: 4, daysMax: 6,
    provinces: ['มหาสารคาม', 'ร้อยเอ็ด', 'มุกดาหาร', 'ยโสธร', 'อำนาจเจริญ', 'นครราชสีมา', 'บุรีรัมย์', 'สุรินทร์', 'ศรีสะเกษ', 'อุบลราชธานี'] },
  { key: 'east', label: 'ภาคตะวันออก', daysMin: 3, daysMax: 4,
    provinces: ['ระยอง', 'จันทบุรี', 'ตราด'] },
  { key: 'andaman', label: 'ภาคใต้-ฝั่งอันดามัน', daysMin: 4, daysMax: 6,
    provinces: ['ระนอง', 'พังงา', 'ภูเก็ต', 'กระบี่', 'ชุมพร'] },
  { key: 'gulf-1', label: 'ภาคใต้-อ่าวไทยตอนบน', daysMin: 3, daysMax: 5,
    provinces: ['เพชรบุรี', 'ประจวบคีรีขันธ์', 'ชุมพร', 'สุราษฎร์ธานี'] },
  { key: 'gulf-2', label: 'ภาคใต้-อ่าวไทยตอนล่าง', daysMin: 4, daysMax: 6,
    provinces: ['สุราษฎร์ธานี', 'นครศรีธรรมราช', 'พัทลุง', 'สงขลา', 'ตรัง', 'สตูล'] },
  { key: 'border', label: 'ชายแดนใต้', daysMin: 4, daysMax: 5,
    provinces: ['สงขลา', 'ปัตตานี', 'ยะลา', 'นราธิวาส'] }
]

const REFUSED = ['แม่ฮ่องสอน']

function groupsOf (province) {
  const p = String(province || '').trim()
  return GROUPS.filter(g => g.provinces.indexOf(p) !== -1).map(g => g.key)
}
function isRefused (province) { return REFUSED.indexOf(String(province || '').trim()) !== -1 }
function groupOf (key) { return GROUPS.find(g => g.key === key) || null }
function groupLabel (key) { const g = groupOf(key); return g ? g.label : String(key || '') }
// A group's provinces for a card or dialog to list -- the labels alone do not say
// whether ชุมพร is on the Andaman run. `also` names the OTHER groups an overlap
// province is in, so the view can mark it and say where else it can go.
function provincesOf (key) {
  const g = groupOf(key)
  if (!g) return []
  return g.provinces.map(name => ({ name, also: groupsOf(name).filter(k => k !== key).map(groupLabel) }))
}
function daysOf (keys) {
  return (keys || []).reduce((m, k) => { const g = groupOf(k); return g ? Math.max(m, g.daysMax) : m }, 0)
}

// The board's settings. Written only by the setUpcountryConfig callable
// (functions/upcountryConfig.js keeps the same defaults -- the test asserts it).
const CONFIG_DEFAULTS = {
  minValue: 150000,
  standardValue: 200000,
  capacityCbm: 7,
  fillFloor: 0.5,
  cbmPerItem: 0.14,
  departures: { mon: 1, tue: 1, wed: 2, thu: 1 }
}

function positive (v, fallback) {
  const n = Number(v)
  return isFinite(n) && n > 0 ? n : fallback
}

function normalizeConfig (doc) {
  const d = doc || {}
  const dep = Object.assign({}, CONFIG_DEFAULTS.departures, d.departures || {})
  return {
    minValue: positive(d.minValue, CONFIG_DEFAULTS.minValue),
    standardValue: positive(d.standardValue, CONFIG_DEFAULTS.standardValue),
    capacityCbm: positive(d.capacityCbm, CONFIG_DEFAULTS.capacityCbm),
    fillFloor: positive(d.fillFloor, CONFIG_DEFAULTS.fillFloor),
    cbmPerItem: positive(d.cbmPerItem, CONFIG_DEFAULTS.cbmPerItem),
    departures: { mon: Number(dep.mon) || 0, tue: Number(dep.tue) || 0, wed: Number(dep.wed) || 0, thu: Number(dep.thu) || 0 }
  }
}

function isUpcountry (b) { return !!b && b.fleet === 'large' }
function isPooled (b) { return isUpcountry(b) && R.isActive(b.status) && !b.upTripId }

const round = (n, d) => Math.round(n * Math.pow(10, d)) / Math.pow(10, d)

// Items are the unit people read; volume is the arithmetic underneath. SAP's
// own volume where the SO has one, the 0.14-per-item rule where it does not.
// A service-only SO (a repair visit) carries nothing out, so it counts no items
// and no volume -- not the 0.14-per-item guess for an SO without volume.
function orderFigures (b, cfg) {
  const c = cfg || CONFIG_DEFAULTS
  // A pickup carries nothing out and is worth nothing to the trip minimum. An
  // exchange is judged on its SO alone: the old items ride back in a truck
  // already emptied by its drops (decided 2026-10-08).
  if (RJ.isReturnOnly(b)) return { items: 0, volume: 0, value: 0, count: 0 }
  const rows = b && b.prodList
  if (Array.isArray(rows) && rows.length && rows.every(I.isServiceRow)) {
    return { items: 0, volume: 0, value: Number(b.orderValue) || 0, count: 0 }
  }
  const sapVolume = Number(b && b.totalVolume) || 0
  const count = Number(b && b.itemCount) || 0
  const volume = sapVolume > 0 ? sapVolume : count * c.cbmPerItem
  // `items` is volume in standard items -- arithmetic only, never shown: next to
  // "/ 50" a 0.126 CBM kiosk read as "0.9 ชิ้น". `count` is the SO's real item
  // count, which is what staff read; volume in CBM is the truck's measure.
  return { items: round(volume / c.cbmPerItem, 1), volume: round(volume, 3), value: Number(b && b.orderValue) || 0, count: count }
}

// --- weeks -------------------------------------------------------------------
const m = ymd => moment.tz(ymd, 'YYYY-MM-DD', R.TZ)
function addDays (ymd, n) { return m(ymd).add(n, 'days').format('YYYY-MM-DD') }
function mondayOf (ymd) { const d = m(ymd); return d.subtract(d.isoWeekday() - 1, 'days').format('YYYY-MM-DD') }

// By Thursday dispatch has next week's trucks and routes, so from Thursday the
// first week a new order can still join is the one after.
function firstOfferedWeek (today) {
  const dow = m(today).isoWeekday()
  return addDays(mondayOf(today), dow <= 3 ? 7 : 14)
}

const MONTHS_TH = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.']
// Monday to Saturday: no delivery on Sunday.
function weekLabel (monday) {
  const a = m(monday)
  const b = m(monday).add(5, 'days')
  return a.month() === b.month()
    ? `${a.date()}–${b.date()} ${MONTHS_TH[b.month()]}`
    : `${a.date()} ${MONTHS_TH[a.month()]}–${b.date()} ${MONTHS_TH[b.month()]}`
}

// When an order was booked, for the pool row: the pool is first come, first
// served within a week, and the SO numbers do not show that order.
function bookedLabel (iso) {
  const d = moment(String(iso || ''), moment.ISO_8601, true)
  if (!iso || !d.isValid()) return ''
  const t = d.tz(R.TZ)
  return `จอง ${t.date()} ${MONTHS_TH[t.month()]} ${t.format('HH:mm')}`
}

function weekOptions (today, horizonDays) {
  const last = R.horizonDate(today, horizonDays)
  const out = []
  for (let w = firstOfferedWeek(today); w <= last; w = addDays(w, 7)) out.push({ week: w, label: weekLabel(w) })
  return out
}

function fail (reason) { return { ok: false, reason: reason } }

// GetSo's orderValue as a number, or null when SAP sent none. ฿0 is a real
// answer -- a service visit, or display stock sent to an exhibition -- and is
// booked like any other; only a missing value (a connection without the
// deliveryDB change) is refused, because then nobody knows what the order is worth.
function sapOrderValue (v) {
  if (v == null || String(v).trim() === '') return null
  const n = Number(v)
  return isFinite(n) && n >= 0 ? n : null
}

// What createUpOrder refuses. `upProvince` is the province the SO's address
// resolved to -- stored, so a postponed order can get both its groups back.
function checkUpOrder (o, today) {
  const returnOnly = RJ.isReturnOnly(o)
  const ref = returnOnly ? (o && o.rtNo) : (o && o.soNo)
  if (!String(ref || '').trim()) return fail('UP_NO_SO')
  if (isRefused(o.upProvince)) return fail('UP_REFUSED_PROVINCE')
  if (!groupsOf(o.upProvince).length) return fail('UP_NO_GROUP')
  const w = String(o.earliestWeek || '')
  if (!/^\d{4}-\d{2}-\d{2}$/.test(w) || mondayOf(w) !== w || w < firstOfferedWeek(today)) return fail('UP_BAD_WEEK')
  if (!returnOnly && sapOrderValue(o.orderValue) === null) return fail('UP_NO_VALUE')
  return { ok: true }
}

function canManageOrder (b, role, identities) {
  return R.isStaffRole(role) || ownedBy(b && b.user, identities)
}

// The จองวันส่งสินค้า card: a user's own live up-country orders, which the day
// list cannot show (a pooled order has no date). Staff see their own too -- the
// whole pool is the รถใหญ่ต่างจังหวัด page's job. An order on a trip drops off
// MY_ORDERS_KEEP_DAYS after its date, so the card does not grow forever.
const MY_ORDERS_KEEP_DAYS = 7

function myOrders (rows, identities, today) {
  const cutoff = addDays(today, -MY_ORDERS_KEEP_DAYS)
  const live = (rows || []).filter(b => isUpcountry(b) && R.isActive(b.status) && ownedBy(b.user, identities))
  const pooled = live.filter(b => !b.upTripId).sort(byWaiting)
  const onTrip = live.filter(b => b.upTripId && String(b.date) >= cutoff)
    .sort((a, b) => String(a.date).localeCompare(String(b.date)))
  return pooled.concat(onTrip)
}

// What a failed action tells the user. A booking rule (BookingError) speaks its
// own Thai; a callable that is not deployed or cannot be reached surfaces from
// the SDK as "internal" after a CORS failure, which told staff nothing.
const UNREACHABLE_CODES = ['internal', 'unavailable', 'not-found', 'deadline-exceeded']
const DENIED_CODES = ['permission-denied', 'unauthenticated']

function actionMessage (e) {
  if (!e) return 'เกิดข้อผิดพลาด โปรดลองใหม่'
  if (e.reason) return R.errorMessage(e.reason)
  const code = String(e.code || '').replace(/^functions\//, '')
  if (UNREACHABLE_CODES.indexOf(code) !== -1) {
    return 'เชื่อมต่อระบบไม่ได้ — ฟังก์ชันอาจยังไม่ได้ติดตั้ง หรือเครือข่ายขัดข้อง โปรดแจ้งฝ่ายไอที'
  }
  if (DENIED_CODES.indexOf(code) !== -1) return 'ไม่มีสิทธิ์ทำรายการนี้ (เฉพาะผู้ดูแลระบบ)'
  return e.message || String(e)
}

// อัปเดตจาก SAP: what a waiting order's figures become, given what GetSo and
// GetProdList return now. An order keeps the figures it was booked with, so a
// corrected item master (PT-04's volume) or an edited SO reaches it only here.
// A SAP state that should stop the order (cancelled, closed, already on a
// dispatch sheet) is REPORTED for a person to decide, never acted on; a
// missing answer keeps the booked figure rather than blanking it.
const REFRESH_FIELDS = ['itemCount', 'totalVolume', 'orderValue']

function sapRefresh (order, so, rows, rtPatch) {
  const b = order || {}
  const from = {}
  REFRESH_FIELDS.forEach(k => { from[k] = b[k] })
  // The ใบจัดรถ SAP already holds the SO on (closed or not, never a cancelled
  // SO): such an order was dispatched by hand in managecar and is offered to
  // staff to take out of the pool (นำออกจากคิว, upcountryService.retireDispatched).
  const dispatched = so && so.canceled !== 'Y' && so.transNo ? String(so.transNo).trim() : null
  const keep = problem => ({ changed: false, patch: {}, from, problem, dispatched })
  if (RJ.isReturnOnly(b)) return keep('รับคืน — ไม่มี SO ให้อัปเดต')
  if (!so) return keep('ไม่พบ SO ใน SAP')
  if (so.canceled === 'Y') return keep('SO ถูกยกเลิกใน SAP')
  if (so.status === 'C') return keep('SO ถูกปิดใน SAP')
  if (so.transNo) return keep(`อยู่ในใบจัดรถ ${so.transNo} แล้ว`)
  if (!Array.isArray(rows) || !rows.length) return keep('SAP ไม่ส่งรายการสินค้า — คงค่าเดิม')

  const value = sapOrderValue(so.orderValue)
  const patch = {
    itemCount: I.itemsFromRows(rows),
    totalVolume: I.volumeFromRows(rows),
    orderValue: value !== null ? value : (Number(b.orderValue) || 0),
    prodList: rows,
    productList: I.productListText(rows)
  }
  // An RT linked in SAP after the order was booked (the 226100015 case) joins
  // here. One already on the order is never replaced: that is a person's call.
  const addsRt = !!(rtPatch && rtPatch.rtNo && !b.rtNo)
  if (addsRt) Object.assign(patch, rtPatch)
  const changed = addsRt || REFRESH_FIELDS.some(k => Number(patch[k]) !== Number(b[k]))
  return { changed, patch, from, problem: value !== null ? null : 'SAP ไม่ส่งมูลค่า — คงค่าเดิม', dispatched }
}

// The order's line on ส่งเข้าจัดรถ's list: what dispatch keys into managecar.
function handoverRef (o) {
  if (RJ.isReturnOnly(o)) return `RT ${o.rtNo} (รับคืน)`
  return o && o.rtNo ? `${o.soNo} + RT ${o.rtNo}` : String((o && o.soNo) || '')
}

// The same, labelled for a person: 'SO 226100015 + RT 226100777',
// 'SO 226100015', or 'RT 226100777 (รับคืน)' -- a pickup has no SO to name.
function orderRef (o) {
  const ref = handoverRef(o)
  return o && o.soNo ? `SO ${ref}` : ref
}

// What a shop is told about a group: never another shop's money.
function shopPanelStatus (card) {
  return card && card.ready ? 'ครบเกณฑ์ รอจัดรถ' : 'ยังไม่ครบรอบ'
}

// --- board and trips ---------------------------------------------------------
function totals (orders, cfg) {
  return (orders || []).reduce((t, b) => {
    const f = orderFigures(b, cfg)
    return { orders: t.orders + 1, items: round(t.items + f.items, 1), volume: round(t.volume + f.volume, 3), value: t.value + f.value, count: t.count + f.count }
  }, { orders: 0, items: 0, volume: 0, value: 0, count: 0 })
}

// The order list of one trip: by province, so one stop's orders sit together for
// loading and for the dispatch copy, then by SO number. An order with no SO sorts last.
function tripOrderSort (orders) {
  const so = b => (b.soNo ? Number(b.soNo) : Infinity)
  return (orders || []).slice().sort((a, b) =>
    String(a.upProvince || '').localeCompare(String(b.upProvince || ''), 'th') || so(a) - so(b))
}

const byWaiting = (a, b) => String(a.earliestWeek).localeCompare(String(b.earliestWeek)) ||
  String(a.createdAt).localeCompare(String(b.createdAt))

// One card per group. An overlap order counts toward both groups as "shared",
// so a group can never look ready only on orders another group may take.
function boardCards (orders, cfg) {
  const c = cfg || CONFIG_DEFAULTS
  const pooled = (orders || []).filter(isPooled)
  return GROUPS.map(g => {
    const mine = pooled.filter(b => (b.upGroup || []).indexOf(g.key) !== -1)
    const ownRows = mine.filter(b => b.upGroup.length === 1).sort(byWaiting)
    const sharedRows = mine.filter(b => b.upGroup.length > 1).sort(byWaiting)
    const own = totals(ownRows, c)
    const shared = totals(sharedRows, c)
    const ready = own.value >= c.minValue ? 'own'
      : (own.value + shared.value >= c.minValue && shared.orders ? 'shared' : null)
    return { key: g.key, label: g.label, own, shared, ready, pool: ownRows.concat(sharedRows) }
  })
}

function eligible (orders, groups, startDate) {
  const week = mondayOf(startDate)
  return (orders || []).filter(b => isPooled(b) &&
    (b.upGroup || []).some(k => groups.indexOf(k) !== -1) &&
    String(b.earliestWeek) <= week).sort(byWaiting)
}

// The earliest week a pooled customer of these groups can receive, or null.
function firstReadyWeek (orders, groups) {
  const weeks = (orders || []).filter(b => isPooled(b) && (b.upGroup || []).some(k => groups.indexOf(k) !== -1))
    .map(b => String(b.earliestWeek)).sort()
  return weeks.length ? weeks[0] : null
}

// Where the fix dialog opens: the first week anyone in the group can receive,
// never before next week. Opening on next Monday regardless listed nothing when
// the group's first order was ready a week later.
function defaultFixStart (orders, groups, today) {
  const next = addDays(mondayOf(today), 7)
  const first = firstReadyWeek(orders, groups)
  return first && first > next ? first : next
}

// A trip card on รถใหญ่ต่างจังหวัด opens by itself when the trip leaves within
// TRIP_OPEN_DAYS (or is already on the road) -- the ones being worked on.
// Every other card shows its header only, until clicked.
const TRIP_OPEN_DAYS = 3
function tripOpenByDefault (trip, today) {
  return String((trip && trip.startDate) || '') <= addDays(today, TRIP_OPEN_DAYS)
}

// A trip past its return date that nobody finished: 'NOT_CLOSED' when it was
// sent and never closed (ปิดรอบ is what frees the truck early), 'NOT_SENT' when
// it was never even marked sent. null for anything else.
function overdueTrip (trip, today) {
  if (!trip || !trip.returnDate || trip.returnDate >= today) return null
  if (trip.status === 'sent') return 'NOT_CLOSED'
  if (trip.status === 'fixed') return 'NOT_SENT'
  return null
}

// A group card on รถใหญ่ต่างจังหวัด shows its order list by itself only when the
// group is ready to fix (own or with shared orders): those are the ones staff act
// on. The bars and จัดรอบ always show; the list opens on a click.
function groupOpenByDefault (card) { return !!(card && card.ready) }

// The trip dialogs' date checks, shared with the service so a page that skips
// the dialog is refused too. They replaced window.prompt, where a typo in ปิดรอบ's
// date could free a truck early.
const YMD = /^\d{4}-\d{2}-\d{2}$/
const isYmd = d => YMD.test(String(d || '')) && moment.tz(d, 'YYYY-MM-DD', true, R.TZ).isValid()

function checkReturnDate (trip, date, holds) {
  if (!isYmd(date) || date < trip.startDate) return fail('UP_BAD_RETURN')
  if (holdsOverlap(holds, loadingDay(trip.startDate), date, trip.id)) return fail('UP_TRUCK_BUSY')
  return { ok: true }
}

// The truck came back on a day between its departure and today.
function checkActualReturn (trip, date, today) {
  if (!isYmd(date) || date < trip.startDate || date > today) return fail('UP_BAD_ACTUAL_RETURN')
  return { ok: true }
}

// The ฿ minimum is a warning, not a refusal: a sale event can fill a truck of
// its own with display stock worth ฿0, and delivery decides whether a trip
// under the minimum goes. The warning is kept in the trip's history.
function fixWarnings (t, cfg) {
  const out = []
  if (t.value < cfg.minValue) out.push('BELOW_MIN')
  if (t.volume < cfg.capacityCbm * cfg.fillFloor) out.push('LOW_FILL')
  if (t.volume > cfg.capacityCbm) out.push('OVER_CAPACITY')
  return out
}

function loadingDay (start) { return addDays(start, -1) }
function defaultReturnDate (start, groups) { return addDays(start, Math.max(1, daysOf(groups)) - 1) }

// A truck is held from its loading day to its return date, both inclusive.
function holdsOverlap (holds, from, to, exceptTripId) {
  return (holds || []).find(h => h.tripId !== exceptTripId && h.from <= to && from <= h.to) || null
}
function holdsAfterRelease (holds, tripId) { return (holds || []).filter(h => h.tripId !== tripId) }
function holdsAfterFix (holds, tripId, from, to) {
  return holdsAfterRelease(holds, tripId).concat([{ tripId, from, to }])
}

function narrowGroup (upGroup, groups) {
  const hit = (upGroup || []).find(k => groups.indexOf(k) !== -1)
  return hit ? [hit] : (upGroup || [])
}

function checkFix (f) {
  const c = f.cfg || CONFIG_DEFAULTS
  if (!f.groups || !f.groups.length) return fail('UP_NO_GROUP')
  if (!f.orders || !f.orders.length) return fail('UP_NO_ORDERS')
  if (!String(f.regNum || '').trim()) return fail('UP_NO_CAR')
  if (loadingDay(f.startDate) < f.today) return fail('UP_PAST')
  if (R.isSunday(f.startDate)) return fail('UP_SUNDAY')
  if (!f.returnDate || f.returnDate < f.startDate) return fail('UP_BAD_RETURN')
  const week = mondayOf(f.startDate)
  for (const b of f.orders) {
    if (!isPooled(b)) return fail('UP_NOT_POOLED')
    if (b.proposalStatus === 'pending' && b.proposal) return fail('PROPOSAL_PENDING')
    if (!(b.upGroup || []).some(k => f.groups.indexOf(k) !== -1)) return fail('UP_WRONG_GROUP')
    if (String(b.earliestWeek) > week) return fail('UP_TOO_EARLY_WEEK')
  }
  const t = totals(f.orders, c)
  if (holdsOverlap(f.holds, loadingDay(f.startDate), f.returnDate, f.tripId || null)) return fail('UP_TRUCK_BUSY')
  return { ok: true, warnings: fixWarnings(t, c) }
}

function tripFigures (orderIds, items, volume, value, cfg) {
  return { orderIds, items: round(items, 1), volume: round(volume, 3), value, belowMin: value < cfg.minValue }
}
function tripAfterAdd (trip, order, cfg) {
  const ids = trip.orderIds || []
  if (ids.indexOf(order.id) !== -1) return tripFigures(ids, trip.items, trip.volume, trip.value, cfg)
  const f = orderFigures(order, cfg)
  return tripFigures(ids.concat([order.id]), trip.items + f.items, trip.volume + f.volume, trip.value + f.value, cfg)
}
function tripAfterRemove (trip, orderId, order, cfg) {
  const ids = trip.orderIds || []
  if (ids.indexOf(orderId) === -1) return tripFigures(ids, trip.items, trip.volume, trip.value, cfg)
  const f = orderFigures(order, cfg)
  return tripFigures(ids.filter(id => id !== orderId), Math.max(0, trip.items - f.items),
    Math.max(0, trip.volume - f.volume), Math.max(0, trip.value - f.value), cfg)
}

// Orders may join or leave only while the trip is `fixed`: once it is `sent`
// the route may already be keyed in จัดรถ, which this app cannot see.
function checkTripEditable (trip) {
  if (!trip || trip.status === 'cancelled' || trip.status === 'done') return fail('UP_TRIP_CLOSED')
  if (trip.status === 'sent') return fail('UP_TRIP_SENT')
  return { ok: true }
}

// A trip sent to จัดรถ by this app (it carries the trip number) may still be changed
// until its loading day is over; from the departure day it is dispatch's alone.
function checkSentEdit (trip, today) {
  if (!trip || trip.status !== 'sent' || !trip.transNo) return fail('UP_TRIP_SENT')
  if (String(today) > loadingDay(trip.startDate)) return fail('UP_SENT_LOCKED')
  return { ok: true }
}
// Whether an order may join: a fixed trip as before, a sent one inside the window.
function checkAddable (trip, today) {
  if (trip && trip.status === 'sent') return checkSentEdit(trip, today)
  return checkTripEditable(trip)
}

// Booking a new order straight onto a trip already fixed -- the one rule the
// booking dialog and createUpOrder share, so a trip the dialog offers is one
// the save accepts. Unlike staff's + เพิ่มออเดอร์, a booking may not pass the
// truck's volume: a full trip sends the booker to the pool for a later week.
function checkJoinTrip (trip, order, cfg, today) {
  const c = cfg || CONFIG_DEFAULTS
  const editable = checkTripEditable(trip)
  if (!editable.ok) return editable
  if (!groupsOf(order && order.upProvince).some(k => (trip.groups || []).indexOf(k) !== -1)) return fail('UP_WRONG_GROUP')
  if (mondayOf(trip.startDate) < firstOfferedWeek(today)) return fail('UP_BAD_WEEK')
  const after = round((Number(trip.volume) || 0) + orderFigures(order, c).volume, 3)
  if (after > c.capacityCbm) return fail('UP_TRIP_FULL')
  return { ok: true }
}

// The dialog's list: fixed trips of the order's groups, by date. A trip too soon
// to offer or of another group is left out; a full one is kept, to show เต็ม.
function tripChoices (trips, order, cfg, today) {
  const c = cfg || CONFIG_DEFAULTS
  const own = orderFigures(order, c).volume
  const choices = (trips || [])
    .filter(t => t && t.status === 'fixed')
    .map(t => {
      const r = checkJoinTrip(t, order, c, today)
      const volume = Number(t.volume) || 0
      return { trip: t, ok: r.ok, reason: r.ok ? null : r.reason, volume, volumeAfter: round(volume + own, 3) }
    })
    .filter(x => x.reason !== 'UP_WRONG_GROUP' && x.reason !== 'UP_BAD_WEEK')
    .sort((a, b) => String(a.trip.startDate).localeCompare(String(b.trip.startDate)))
  const first = choices.find(x => x.ok)
  return { choices, preselect: first ? first.trip.id : null }
}

// The booker's choice against the live list: the trip while it can still take
// the order, else why not -- 'gone' when it left the list (sent, cancelled), or
// checkJoinTrip's reason. The dialog SAYS the problem and blocks บันทึก; it never
// moves the choice to the pool or to another trip on its own, because the
// booker may already have told the customer that trip's date.
function choiceState (choice, choices) {
  if (!choice || choice === 'pool') return { trip: null, problem: null }
  const c = (choices || []).find(x => x.trip.id === choice)
  if (!c) return { trip: null, problem: 'gone' }
  return c.ok ? { trip: c.trip, problem: null } : { trip: null, problem: c.reason }
}
function choiceProblemText (problem) {
  if (!problem) return ''
  const what = problem === 'UP_TRIP_FULL' ? 'เต็มแล้ว' : 'ปิดรับหรือส่งต่อให้จัดรถแล้ว'
  return `รอบที่เลือก${what} — โปรดเลือกรอบอื่นหรือเข้าคิวรอจัดรอบ`
}

const DOW_TH = ['อา.', 'จ.', 'อ.', 'พ.', 'พฤ.', 'ศ.', 'ส.']
const dayMonth = ymd => { const d = m(ymd); return `${d.date()}/${d.month() + 1}` }
function tripLabel (trip) {
  const t = trip || {}
  return `${DOW_TH[m(t.startDate).day()]} ${dayMonth(t.startDate)} → ${dayMonth(t.returnDate)} · ` +
    `${(t.groups || []).map(groupLabel).join(' + ')} · ${t.regNum || ''}`
}

module.exports = {
  GROUPS, REFUSED, groupsOf, isRefused, groupLabel, provincesOf, daysOf,
  CONFIG_DEFAULTS, normalizeConfig, isUpcountry, isPooled, orderFigures,
  addDays, mondayOf, firstOfferedWeek, weekLabel, bookedLabel, weekOptions,
  sapOrderValue, checkUpOrder, canManageOrder, actionMessage, sapRefresh, handoverRef, orderRef, shopPanelStatus, fail, MY_ORDERS_KEEP_DAYS, myOrders,
  totals, tripOrderSort, boardCards, eligible, fixWarnings, loadingDay, defaultReturnDate,
  holdsOverlap, holdsAfterFix, holdsAfterRelease, narrowGroup,
  checkReturnDate, checkActualReturn, firstReadyWeek, defaultFixStart, TRIP_OPEN_DAYS, tripOpenByDefault, overdueTrip, groupOpenByDefault,
  checkFix, tripAfterAdd, tripAfterRemove, checkTripEditable, checkSentEdit, checkAddable, checkJoinTrip, tripChoices, tripLabel, choiceState, choiceProblemText
}
